/**
 * Policy page content component for privacy, terms, and impressum.
 */
import React from 'react';

const PRIVACY_POLICY = `# Privacy Policy

**Zeytune**
Last updated: March 2026

Zeytune ("we", "us", or "our") operates the website https://zeytune.de. This Privacy Policy explains how we collect, use, store, and protect your personal data when you use our service, and informs you of your rights under the General Data Protection Regulation (GDPR).

We are committed to protecting your privacy and handling your data responsibly and transparently.

---

## Topics

- [What data do we collect?](#1-what-data-do-we-collect)
- [How do we collect your data?](#2-how-do-we-collect-your-data)
- [How will we use your data?](#3-how-will-we-use-your-data)
- [Legal basis for processing](#4-legal-basis-for-processing)
- [How do we store your data?](#5-how-do-we-store-your-data)
- [Data sharing and third parties](#6-data-sharing-and-third-parties)
- [Marketing](#7-marketing)
- [What are your data protection rights?](#8-what-are-your-data-protection-rights)
- [Cookies](#9-cookies)
- [Privacy policies of other websites](#10-privacy-policies-of-other-websites)
- [Changes to our privacy policy](#11-changes-to-our-privacy-policy)
- [How to contact us](#12-how-to-contact-us)
- [How to contact the appropriate authorities](#13-how-to-contact-the-appropriate-authorities)

---

## 1. What data do we collect?

When you sign in to Zeytune using your LinkedIn account, we collect the following personal data:

- Full name (as provided by your LinkedIn profile)
- Email address (as provided by your LinkedIn profile)

We do not collect your LinkedIn password. Authentication is handled entirely by LinkedIn's OAuth 2.0 service. We do not collect any additional LinkedIn profile data beyond your name and email address.

If you choose to use the GitHub integration feature, you may optionally provide a GitHub Personal Access Token. **This token is never stored by Zeytune.** It is held in memory only for the duration of your active session and is permanently discarded once the session ends.

---

## 2. How do we collect your data?

You directly provide Zeytune with data when you:

- Sign in to our service using the "Sign in with LinkedIn" button, which initiates an OAuth 2.0 authentication flow managed by LinkedIn.
- Optionally enter a GitHub Personal Access Token to enable the GitHub Projects integration feature. This token is used solely to push issues to your GitHub Projects on your behalf and is never written to disk or retained after your session ends.

We also automatically collect certain technical data when you visit our website:

- Usage and analytics data (via Google Analytics, including pages visited, session duration, browser type, and approximate location derived from your IP address)
- Cookies and similar tracking technologies (see the [Cookies](#9-cookies) section below)

---

## 3. How will we use your data?

Zeytune collects your data so that we can:

- Create and manage your personal account on Zeytune
- Identify you when you sign in to the service
- Provide you with access to the features and functionality of Zeytune
- Improve our website and service through anonymised analytics data
- Push issues to your GitHub Projects on your behalf, using your GitHub Personal Access Token for the duration of your session only

We do not use your data for automated decision-making or profiling.

---

## 4. Legal Basis for Processing

Under the GDPR, we process your personal data on the following legal bases:

**Contract (Article 6(1)(b) GDPR):** Processing your name and email is necessary to create and maintain your Zeytune account and provide you with the service you have requested.

**Legitimate Interests (Article 6(1)(f) GDPR):** We use Google Analytics to understand how our service is used and to improve it. This is based on our legitimate interest in improving our product, balanced against your right to privacy. Google Analytics data is anonymised and aggregated.

---

## 5. How do we store your data?

Zeytune stores your personal data (name and email address) securely in MongoDB Atlas, a cloud-hosted database service operated by MongoDB, Inc. Data is stored on servers within the European Union where possible.

MongoDB Atlas applies industry-standard security measures including encryption at rest and in transit, access controls, and regular security audits.

We will retain your personal data for as long as your Zeytune account remains active. If you request deletion of your account, we will delete your personal data from our database within 30 days of receiving your request.

---

## 6. Data Sharing and Third Parties

Zeytune uses the following third-party services that may process your data:

**LinkedIn (Microsoft Corporation):** Provides OAuth 2.0 authentication. LinkedIn processes your data in accordance with their own Privacy Policy at https://www.linkedin.com/legal/privacy-policy.

**MongoDB Atlas (MongoDB, Inc.):** Provides secure cloud database hosting for your account data. MongoDB's privacy policy is available at https://www.mongodb.com/legal/privacy-policy.

**GitHub (GitHub, Inc. / Microsoft Corporation):** If you use the GitHub integration feature, your Personal Access Token is used in-session to communicate with the GitHub API on your behalf. The token is never stored by Zeytune and is discarded at the end of your session. GitHub processes this request in accordance with their Privacy Policy at https://docs.github.com/en/site-policy/privacy-policies/github-general-privacy-statement.

**Google Analytics (Google LLC):** Provides website analytics. Google may process anonymised usage data in the United States. Google is certified under the EU-U.S. Data Privacy Framework. You can opt out of Google Analytics tracking by using the Google Analytics Opt-out Browser Add-on at https://tools.google.com/dlpage/gaoptout.

We do not sell your personal data to any third parties. Your GitHub Personal Access Token is never transmitted to any party other than GitHub's own API, and only when you explicitly trigger the integration. We do not share your personal data with any other organisations.

---

## 7. Marketing

Zeytune does not currently use your data for marketing purposes and does not send marketing emails. We will not contact you for marketing purposes without your explicit prior consent.

---

## 8. What are your data protection rights?

As a user based in the European Union, you are entitled to the following rights under the GDPR:

**The right to access:** You have the right to request a copy of the personal data we hold about you.

**The right to rectification:** You have the right to request that we correct any personal data you believe is inaccurate or incomplete.

**The right to erasure:** You have the right to request that we delete your personal data. You can do this by deleting your account from within the app or by contacting us directly.

**The right to restrict processing:** You have the right to request that we restrict the processing of your personal data under certain conditions.

**The right to object to processing:** You have the right to object to our processing of your personal data where we rely on legitimate interests as the legal basis.

**The right to data portability:** You have the right to request that we transfer your personal data to another organisation, or directly to you, in a structured, commonly used, and machine-readable format.

We will respond to any requests within one month. To exercise any of these rights, please contact us using the details in the [How to contact us](#12-how-to-contact-us) section below.

---

## 9. Cookies

### What are cookies?

Cookies are small text files placed on your device by your browser when you visit a website. They are used to remember your preferences and to collect information about how you use a website.

### How do we use cookies?

Zeytune uses cookies for the following purposes:

- Keeping you signed in to your account (session cookies)
- Collecting anonymised analytics data via Google Analytics to help us understand how our service is used and to improve the service

### Types of cookies we use

**Functionality cookies:** These are necessary for you to remain signed in during your session. Without these cookies, the service cannot function.

**Analytics cookies (Google Analytics):** These cookies help us understand how visitors interact with our website. All data collected is anonymised and aggregated. No personally identifiable information is sent to Google Analytics.

### How to manage cookies

You can configure your browser to refuse cookies or to alert you when cookies are being sent. Please note that if you disable cookies, some parts of Zeytune may not function correctly.

For more information about cookies and how to manage them, visit https://www.allaboutcookies.org.

---

## 10. Privacy policies of other websites

The Zeytune website may contain links to other websites. This Privacy Policy applies only to https://zeytune.de. We encourage you to read the privacy policies of any other websites you visit.

---

## 11. Changes to our privacy policy

We keep this Privacy Policy under regular review and will post any updates on this page. The date at the top of this page indicates when the policy was last updated.

Where changes are significant, we will make reasonable efforts to notify you, for example by displaying a notice within the app.

---

## 12. How to contact us

If you have any questions about this Privacy Policy, the data we hold about you, or you would like to exercise any of your data protection rights, please contact us:

**Email:** farhan3d@gmail.com
**Website:** https://zeytune.de
**Address:** Wieselweg 24, 85540 Haar, Germany

---

## 13. How to contact the appropriate authorities

If you are not satisfied with how we handle your data, or if you feel your data protection rights have not been upheld, you have the right to lodge a complaint with the competent supervisory authority in Germany:

**Bayerisches Landesamt für Datenschutzaufsicht (BayLDA)**
*(Bavarian State Office for Data Protection Supervision)*
**Address:** Promenade 18, 91522 Ansbach, Germany
**Phone:** +49 (0) 981 180093-0
**Email:** poststelle@lda.bayern.de
**Website:** https://www.lda.bayern.de

As a Haar/Munich-area operator, the BayLDA is your primary supervisory authority. You may also contact the national authority, the Bundesbeauftragte für den Datenschutz und die Informationsfreiheit (BfDI), if your concern crosses federal boundaries.

---

# Privacy Policy - Impressum

*Angaben gemäß § 5 TMG / Information required under § 5 TMG*

**Betreiber / Operator:** Muhammad Farhan (Zeytune)
**Anschrift / Address:** Wieselweg 24, 85540 Haar, Germany
**E-Mail:** farhan3d@gmail.com
**Website:** https://zeytune.de

**Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV / Responsible for content under § 55 para. 2 RStV:**
Muhammad Farhan, Wieselweg 24, 85540 Haar, Germany

**Haftungsausschluss / Disclaimer:**
Despite careful content control, we assume no liability for the content of external links. The operators of linked pages are solely responsible for their content.
`;

const TERMS_OF_SERVICE = `# Terms of Service

**Zeytune**
Last updated: March 2026

Please read these Terms of Service ("Terms") carefully before using https://zeytune.de ("the Service"), operated by Muhammad Farhan ("we", "us", or "our").

By accessing or using Zeytune, you agree to be bound by these Terms. If you do not agree, please do not use the Service.

---

## Topics

- [Who can use Zeytune](#1-who-can-use-zeytune)
- [Description of the Service](#2-description-of-the-service)
- [Your account](#3-your-account)
- [Your content and intellectual property](#4-your-content-and-intellectual-property)
- [Confidentiality of your data](#5-confidentiality-of-your-data)
- [Data isolation](#6-data-isolation)
- [GitHub integration](#7-github-integration)
- [Acceptable use](#8-acceptable-use)
- [Pricing and free tier](#9-pricing-and-free-tier)
- [Service availability and disclaimers](#10-service-availability-and-disclaimers)
- [Limitation of liability](#11-limitation-of-liability)
- [Account termination and data deletion](#12-account-termination-and-data-deletion)
- [Changes to these Terms](#13-changes-to-these-terms)
- [Governing law](#14-governing-law)
- [How to contact us](#15-how-to-contact-us)

---

## 1. Who can use Zeytune

You may use Zeytune if you are at least 16 years old and capable of entering into a legally binding agreement. By using the Service, you confirm that you meet these requirements.

If you are using Zeytune on behalf of a company or organisation, you confirm that you have the authority to bind that entity to these Terms.

---

## 2. Description of the Service

Zeytune is a web-based planning application that allows individuals and organisations to create and manage strategic project plans using Open Strategy Tools (OST) frameworks. The Service allows you to structure, visualise, and organise project strategies, and optionally push derived artefacts to GitHub Projects.

---

## 3. Your account

You sign in to Zeytune using your LinkedIn account via OAuth 2.0. You are responsible for maintaining the security of your LinkedIn credentials. We are not responsible for any loss or damage arising from unauthorised access to your account resulting from your failure to keep your credentials secure.

You must provide accurate information and keep it up to date. We reserve the right to suspend or terminate accounts that we believe are being used in violation of these Terms.

---

## 4. Your content and intellectual property

**You own everything you create in Zeytune.** All OST plans, strategies, project structures, notes, and any other content you create using the Service ("User Content") remain your exclusive intellectual property.

We do not claim any ownership over your User Content. We do not licence, reproduce, distribute, or exploit your User Content for any purpose other than providing the Service to you.

Specifically, we will never:

- Sell or transfer your User Content to third parties
- Use your User Content to train machine learning or AI models
- Analyse or mine your User Content for commercial purposes
- Share your User Content with other users or organisations without your explicit consent

The only reason we access your User Content is to display it back to you and to store it on your behalf.

---

## 5. Confidentiality of your data

We understand that OST project plans may contain sensitive strategic and business information. We treat all User Content as confidential. We will not disclose your User Content to any third party except:

- Where required by applicable law or a valid legal order
- Where you have explicitly authorised us to do so
- To our infrastructure providers (currently MongoDB Atlas) solely for the purpose of storing and serving your data, under confidentiality obligations

Our infrastructure providers are bound by their own data processing agreements and are not permitted to access or use your content for their own purposes.

---

## 6. Data isolation

Your User Content is fully isolated from that of other users and organisations. No other user or organisation using Zeytune can access, view, or modify your content. We maintain strict access controls at the application and database level to enforce this isolation.

---

## 7. GitHub integration

Zeytune offers an optional feature allowing you to push content to your GitHub Projects using a GitHub Personal Access Token that you provide. By using this feature, you acknowledge that:

- Your token is used solely to perform the GitHub API actions you explicitly trigger
- Your token is held in memory only for the duration of your active session and is never stored by Zeytune
- You are responsible for ensuring your token has only the minimum permissions necessary for the integration
- You can revoke the token at any time via your GitHub account settings, which will immediately disable the integration
- We are not responsible for any actions taken on your GitHub account that result from your providing an overly permissioned token

---

## 8. Acceptable use

You agree not to use Zeytune to:

- Upload or transmit any content that is unlawful, harmful, defamatory, or infringes the intellectual property rights of others
- Attempt to gain unauthorised access to any part of the Service or another user's account
- Use the Service in any way that could damage, overload, or impair its infrastructure
- Reverse engineer, decompile, or attempt to extract the source code of the Service
- Use the Service for any purpose other than its intended use as a strategic planning tool

---

## 9. Pricing and free tier

Zeytune is currently free to use. We reserve the right to introduce paid tiers in the future. If and when paid tiers are introduced:

- Existing users will be given reasonable advance notice before any currently free features become part of a paid tier
- Any pricing changes will be communicated clearly via the Service or by email
- You will never be charged without your explicit consent

---

## 10. Service availability and disclaimers

Zeytune is provided on an **"as is" and "as available"** basis. We do not guarantee that the Service will be uninterrupted, error-free, or free of security vulnerabilities.

As an early-stage application, Zeytune may undergo significant changes, experience downtime, or be temporarily unavailable. We will make reasonable efforts to maintain availability and to communicate planned outages in advance.

**We strongly recommend that you maintain your own backups of any critical strategic content you store in Zeytune.** We are not liable for any loss of data resulting from service outages, bugs, or discontinuation.

---

## 11. Limitation of liability

To the fullest extent permitted by applicable law, Zeytune and its operator shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of or inability to use the Service, including but not limited to loss of data, loss of business, or loss of profits.

Our total liability to you for any claim arising out of or relating to these Terms or the Service shall not exceed the amount you have paid to us in the twelve months preceding the claim. For users on the free tier, this means our liability is limited to the maximum extent permitted by law.

Nothing in these Terms limits our liability for death or personal injury caused by our negligence, fraud, or any other liability that cannot be excluded under German law.

---

## 12. Account termination and data deletion

**You may delete your account at any time** from within the Service. Upon account deletion:

- Your personal data (name and email) will be deleted from our systems within 30 days
- Your User Content (OST plans and project data) will be deleted from our systems within 30 days
- Any data held by third-party infrastructure providers (e.g. MongoDB Atlas) will be deleted in accordance with their data retention policies

We reserve the right to suspend or terminate your account if you violate these Terms. In such cases, we will make reasonable efforts to notify you in advance unless the violation is severe.

**If Zeytune is discontinued** as a service, we will provide at least 30 days notice where reasonably possible, during which time you will be able to export or retrieve your content. After this period, all User Content will be permanently deleted.

---

## 13. Changes to these Terms

We may update these Terms from time to time. When we do, we will update the date at the top of this page. For significant changes, we will make reasonable efforts to notify you via the Service or by email.

Your continued use of Zeytune after changes are posted constitutes your acceptance of the updated Terms.

---

## 14. Governing law

These Terms are governed by the laws of the Federal Republic of Germany. Any disputes arising from these Terms or your use of the Service shall be subject to the exclusive jurisdiction of the courts of Munich, Germany.

If you are a consumer based in the EU, you also benefit from any mandatory consumer protection provisions of the law of your country of residence.

The European Commission provides an online dispute resolution platform at https://ec.europa.eu/consumers/odr, which you may use to resolve disputes.

---

## 15. How to contact us

If you have any questions about these Terms, please contact us:

**Email:** farhan3d@gmail.com
**Website:** https://zeytune.de
**Address:** Wieselweg 24, 85540 Haar, Germany
`;

const IMPRESSUM = `# Impressum

*Angaben gemäß § 5 TMG / Information required under § 5 TMG*

**Betreiber / Operator:** Muhammad Farhan (Zeytune)

**Anschrift / Address:** Wieselweg 24, 85540 Haar, Germany

**E-Mail:** farhan3d@gmail.com

**Website:** https://zeytune.de

**Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV / Responsible for content under § 55 para. 2 RStV:**
Muhammad Farhan, Wieselweg 24, 85540 Haar, Germany

**Haftungsausschluss / Disclaimer:**
Despite careful content control, we assume no liability for the content of external links. The operators of linked pages are solely responsible for their content.`;

function slugify(text: string): string {
    return text
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .trim()
        .replace(/[\s_]+/g, '-');
}

function inlineRender(text: string, key: number) {
    const parts: React.ReactNode[] = [];
    let remaining = text;
    let k = 0;
    while (remaining.length > 0) {
        const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
        const linkMatch = remaining.match(/\[(.+?)\]\((.+?)\)/);
        const boldIdx = boldMatch ? remaining.indexOf(boldMatch[0]) : Infinity;
        const linkIdx = linkMatch ? remaining.indexOf(linkMatch[0]) : Infinity;
        if (boldIdx === Infinity && linkIdx === Infinity) {
            parts.push(remaining);
            break;
        }
        if (boldIdx <= linkIdx && boldMatch) {
            if (boldIdx > 0) parts.push(remaining.substring(0, boldIdx));
            parts.push(<strong key={k++}>{boldMatch[1]}</strong>);
            remaining = remaining.substring(boldIdx + boldMatch[0].length);
        } else if (linkMatch) {
            if (linkIdx > 0) parts.push(remaining.substring(0, linkIdx));
            const href = linkMatch[2];
            if (href.startsWith('#')) {
                const targetId = href.substring(1);
                parts.push(
                    <a
                        key={k++}
                        href={href}
                        onClick={(e) => {
                            e.preventDefault();
                            const el = document.getElementById(targetId);
                            if (el) el.scrollIntoView({ behavior: 'smooth' });
                        }}
                        className='policy-link'
                    >
                        {linkMatch[1]}
                    </a>
                );
            } else {
                parts.push(
                    <a key={k++} href={href} target='_blank' rel='noopener noreferrer' className='policy-external-link'>
                        {linkMatch[1]}
                    </a>
                );
            }
            remaining = remaining.substring(linkIdx + linkMatch[0].length);
        }
    }
    return parts;
}

function renderMarkdown(text: string) {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let listItems: React.ReactNode[] = [];
    let i = 0;

    const flushList = () => {
        if (listItems.length > 0) {
            elements.push(
                <ul key={`ul-${i}`} className='policy-ul'>
                    {listItems}
                </ul>
            );
            listItems = [];
        }
    };

    while (i < lines.length) {
        const line = lines[i];
        if (line.startsWith('# ')) {
            flushList();
            const headingText = line.substring(2);
            elements.push(
                <h1 id={slugify(headingText)} key={i} className='policy-h1'>
                    {inlineRender(headingText, i)}
                </h1>
            );
        } else if (line.startsWith('## ')) {
            flushList();
            const headingText = line.substring(3);
            elements.push(
                <h2 id={slugify(headingText)} key={i} className='policy-h2'>
                    {inlineRender(headingText, i)}
                </h2>
            );
        } else if (line.startsWith('### ')) {
            flushList();
            const headingText = line.substring(4);
            elements.push(
                <h3 id={slugify(headingText)} key={i} className='policy-h3'>
                    {inlineRender(headingText, i)}
                </h3>
            );
        } else if (line.match(/^---+$/)) {
            flushList();
            elements.push(<hr key={i} className='policy-hr' />);
        } else if (line.startsWith('- ')) {
            listItems.push(
                <li key={i} className='policy-li'>
                    {inlineRender(line.substring(2), i)}
                </li>
            );
        } else if (line.trim() === '') {
            flushList();
        } else {
            flushList();
            elements.push(
                <p key={i} className='policy-p'>
                    {inlineRender(line, i)}
                </p>
            );
        }
        i++;
    }
    flushList();
    return elements;
}

type PolicyPageProps = {
    page: 'privacy' | 'terms' | 'impressum';
    onBack: () => void;
};

function PolicyPage({ page, onBack }: PolicyPageProps) {
    let content;
    if (page === 'privacy') content = PRIVACY_POLICY;
    else if (page === 'terms') content = TERMS_OF_SERVICE;
    else if (page === 'impressum') content = IMPRESSUM;
    else content = 'Unknown policy page';

    return (
        <div className='policy-page'>
            <div className='policy-page-inner'>
                <button onClick={onBack} className='policy-back-btn'>
                    ← Back
                </button>
                <div className='policy-body'>{renderMarkdown(content)}</div>
            </div>
        </div>
    );
}

export default PolicyPage;
